// Types
import type { TApiHttpClientRequestConfig } from '~/misc/resource/types';
import type {
  TManagerCompany,
  TManagerSearchCompany,
  TPaginatedCompanyList,
  TPaginatedCompanyPatches,
  TManagerCompanyCreatePayload,
  TManagerCompanyListPayload,
  TManagerCompanySearchPayload,
  TManagerCompanyUpdatePayload,
  TManagerCompanyBulkUsersSubscribePayload,
  TManagerCompanyBulkUsersUnsubscribePayload,
  TManagerCompanyBulkUsersStats,
  TManagerCompanyPatchesPayload,
  TManagerCompanyOpportunityLookup,
  TManagerCompanyOpportunityLookupPayload,
  TManagerCompanyFiltersGetPayload,
  TManagerCompanyFiltersResponse,
  TManagerCompanyFiltersUpdatePayload,
} from '~/modules/manager/types';
import type { TNewPatch, TPatch } from '~/misc/patch/types';
import type { TImportParams, TImportResponse } from '~/shared/import/types';

// Models
import { ManagerResource } from '~/modules/manager/resource/manager-resource.model';

export class ManagerCompanyModel extends ManagerResource {
  protected endpoint: string = 'companies/';

  list(
    payload: TManagerCompanyListPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TPaginatedCompanyList> {
    return this.get<TPaginatedCompanyList>('', payload, requestConfig);
  }

  show(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<TManagerCompany> {
    return this.get<TManagerCompany>(`${companyId}`, undefined, requestConfig);
  }

  search(
    payload: TManagerCompanySearchPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerSearchCompany[]> {
    return this.get<TManagerSearchCompany[]>('search', payload, requestConfig);
  }

  merge(
    hostCompanyId: number,
    fromCompanyId: number,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(`${hostCompanyId}/merge/${fromCompanyId}`, undefined, requestConfig);
  }

  create(
    payload: TManagerCompanyCreatePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerCompany> {
    return this.post<TManagerCompany>('?1', payload, requestConfig);
  }

  update(
    companyId: number,
    payload: TManagerCompanyUpdatePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerCompany> {
    return this.patch<TManagerCompany>(`${companyId}`, payload, requestConfig);
  }

  patches(
    companyId: number,
    payload: TManagerCompanyPatchesPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TPaginatedCompanyPatches> {
    return this.get<TPaginatedCompanyPatches>(`${companyId}/patches`, payload, requestConfig);
  }

  createPatch(
    companyId: number,
    payload: TNewPatch,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TPatch> {
    return this.post<TPatch>(`${companyId}/patches?1`, payload, requestConfig);
  }

  destroyPatches(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.delete<void>(`${companyId}/patches`, undefined, requestConfig);
  }

  import(
    companyId: number,
    params: TImportParams,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TImportResponse> {
    return this.post<TImportResponse>(`${companyId}/import`, params, requestConfig);
  }

  clearNotes(id: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.post<void>(`${id}/clear-notes`, undefined, requestConfig);
  }

  destroy(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.delete<void>(`${companyId}`, undefined, requestConfig);
  }

  suspend(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.put<void>(`${companyId}/suspend`, undefined, requestConfig);
  }

  activate(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.put<void>(`${companyId}/activate`, undefined, requestConfig);
  }

  opportunityLookup(
    companyId: number,
    payload: TManagerCompanyOpportunityLookupPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerCompanyOpportunityLookup> {
    return this.post<TManagerCompanyOpportunityLookup>(
      `${companyId}/opportunity-lookup`,
      payload,
      requestConfig
    );
  }

  bulkUserStats(
    companyId: number,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerCompanyBulkUsersStats> {
    return this.get<TManagerCompanyBulkUsersStats>(
      `${companyId}/user-stats`,
      undefined,
      requestConfig
    );
  }

  bulkSubscribeUsers(
    companyId: number,
    payload: TManagerCompanyBulkUsersSubscribePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(`${companyId}/users/bulk-subscription`, payload, requestConfig);
  }

  bulkUnsubscribeUsers(
    companyId: number,
    payload: TManagerCompanyBulkUsersUnsubscribePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<void> {
    return this.post<void>(`${companyId}/users/bulk-unsubscription`, payload, requestConfig);
  }

  bulkActivateUsers(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.post<void>(`${companyId}/users/bulk-activate`, undefined, requestConfig);
  }

  bulkSuspendUsers(companyId: number, requestConfig?: TApiHttpClientRequestConfig): Promise<void> {
    return this.post<void>(`${companyId}/users/bulk-suspend`, undefined, requestConfig);
  }

  getFilters(
    companyId: number,
    payload: TManagerCompanyFiltersGetPayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerCompanyFiltersResponse> {
    return this.get<TManagerCompanyFiltersResponse>(`${companyId}/filters`, payload, requestConfig);
  }

  updateFilters(
    companyId: number,
    payload: TManagerCompanyFiltersUpdatePayload,
    requestConfig?: TApiHttpClientRequestConfig
  ): Promise<TManagerCompanyFiltersResponse> {
    return this.patch<TManagerCompanyFiltersResponse>(
      `${companyId}/filters`,
      payload,
      requestConfig
    );
  }
}
